import PropTypes from 'prop-types';
import { breakpoints, Inline, Stack, Text, Tooltip, vars } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { CarrierLogo, InformationIconButton } from '@eti/components';
import { TripSegment } from '@eti/types';
import { useMemo } from 'react';
import { constants } from '@eti/utils';
import { useOrderDetails } from '../../../../containers/OrderDetailsContext';
import StatusPerPassenger from './StatusPerPassenger';
import { isStatusSameForAllPassengersOfSegment } from './utils/utils';

const textStyles = css`
  font-size: 0.875rem;
  font-weight: 700;
`;

const bookingNumberInlineStyles = css`
  justify-content: end;

  @media (min-width: ${breakpoints._560}) {
    align-items: end;
    flex-direction: column;
    justify-content: start;
    order: 1;
  }
`;

const carrierLogoStyles = css`
  width: 30px;
`;

const headerInlineStyles = css`
  @media (min-width: ${breakpoints._560}) {
    align-items: center;
    justify-content: space-between;
  }
`;

const outerStackStyles = css`
  border-top: 1px solid ${vars.colors.divider};
  padding-bottom: 16px;
  padding-inline: 16px 8px;
  padding-top: 4px;

  @media (min-width: ${breakpoints._560}) {
    padding-inline-end: 16px;
    padding-top: 8px;
  }
`;

const passengerStackStyles = css`
  padding-inline-start: 38px;
`;

const Segment = ({ boundId, segment }) => {
  const { t } = useTranslation();
  const { travelers } = useOrderDetails();
  const dataTestIdPrefix = `checkin-bound-${boundId}-segment-${segment.segmentId}`;

  const isStatusSameForAllPassengers = useMemo(
    () => isStatusSameForAllPassengersOfSegment(segment),
    [segment],
  );

  const checkInStatusPerPassenger = isStatusSameForAllPassengers
    ? [segment.checkInStatusPerPassenger[0]]
    : segment.checkInStatusPerPassenger;

  return (
    <Stack className={outerStackStyles} data-testid={dataTestIdPrefix} spacing={8}>
      <Inline
        className={headerInlineStyles}
        collapseBelow={breakpoints._560}
        noWrap
        spacing={{ [breakpoints._0]: 4, [breakpoints._560]: 16 }}
      >
        <Inline
          className={bookingNumberInlineStyles}
          spacing={{ [breakpoints._0]: 4, [breakpoints._768]: 0 }}
        >
          <div>{t('OrderDetails.CheckIn.Status.BookingNumber.Label')}</div>
          {segment.carrierBookingNumber?.bookingNumber ? (
            <strong
              className={constants.MOUSE_FLOW_CLASS_NAMES}
              data-testid={`${dataTestIdPrefix}-booking-number`}
            >
              {segment.carrierBookingNumber.bookingNumber}
            </strong>
          ) : (
            <Inline
              data-testid={`${dataTestIdPrefix}-booking-number-unavailable`}
              noWrap
              spacing={4}
            >
              <Text>{t('CheckIn.ReferenceNo.Unavailable.Text')}</Text>
              <Tooltip
                content={t('OrderDetails.Airlines.BookingNumber.Unavailable.Text')}
                placement="top-end"
              >
                <InformationIconButton data-testid="unavailable-info-icon" />
              </Tooltip>
            </Inline>
          )}
        </Inline>
        <Inline alignY="center" noWrap spacing={8}>
          <CarrierLogo
            className={carrierLogoStyles}
            id={segment.operatingCarrier.code}
            imageAlt={segment.operatingCarrier.name}
          />
          <Inline
            className={textStyles}
            data-testid={`${dataTestIdPrefix}-origin-destination`}
            spacing={4}
          >
            <Inline spacing={4}>
              <span>{segment.origin.cityName}</span>
              <span>({segment.origin.code})</span>
            </Inline>
            <div>-</div>
            <Inline spacing={4}>
              <span>{segment.destination.cityName}</span>
              <span>({segment.destination.code})</span>
            </Inline>
          </Inline>
        </Inline>
      </Inline>
      <Stack className={passengerStackStyles}>
        {checkInStatusPerPassenger.map((passenger) => (
          <StatusPerPassenger
            dataTestIdPrefix={dataTestIdPrefix}
            key={passenger.passengerId}
            operatingCarrierName={segment.operatingCarrier.name}
            passenger={passenger}
            shouldShowTextForAllPassengers={isStatusSameForAllPassengers && travelers.length > 1}
          />
        ))}
      </Stack>
    </Stack>
  );
};

Segment.propTypes = {
  boundId: PropTypes.string,
  segment: TripSegment,
};

export default Segment;
